import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Microphone } from 'iconoir-react';

interface VoiceRecorderProps {
  onAudioData: (audioBlob: Blob) => void;
  onTranscription?: (text: string) => void;
  isRecording: boolean;
  onRecordingStateChange: (isRecording: boolean) => void;
  disabled?: boolean;
  compact?: boolean;
  onAudioContextReady?: (audioContext: AudioContext, analyser: AnalyserNode) => void;
  externalCanvasRef?: React.RefObject<HTMLCanvasElement>;
}

const VoiceRecorder: React.FC<VoiceRecorderProps> = ({
  onAudioData,
  onTranscription,
  isRecording,
  onRecordingStateChange,
  disabled = false,
  compact = false,
  onAudioContextReady,
  externalCanvasRef
}) => {
  // State for recording
  const [recordingTime, setRecordingTime] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [isPaused, setIsPaused] = useState(false);

  // Refs for immediate updates
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const recordingTimerRef = useRef<NodeJS.Timeout | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Initialize audio context and analyzer for visualization
  const initializeAudioContext = useCallback(async () => {
    if (audioContextRef.current) return;
    
    try {
      audioContextRef.current = new AudioContext();
      analyserRef.current = audioContextRef.current.createAnalyser();
      analyserRef.current.fftSize = 256;
    } catch (err) {
      console.error('Error initializing audio context:', err);
      setError('Failed to initialize audio system');
    }
  }, []);

  // Start recording
  const startRecording = useCallback(async () => {
    try {
      setError(null);
      
      // Clear previous audio chunks
      audioChunksRef.current = [];
      
      // Request microphone access
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 16000
        }
      });
      
      streamRef.current = mediaStream;
      
      // Initialize audio context if not already done
      if (!audioContextRef.current) {
        await initializeAudioContext();
      }
      
      // Connect stream to analyzer for visualization
      if (audioContextRef.current && analyserRef.current) {
        const source = audioContextRef.current.createMediaStreamSource(mediaStream);
        source.connect(analyserRef.current);
        
        // Notify parent component that audio context is ready
        if (onAudioContextReady) {
          onAudioContextReady(audioContextRef.current, analyserRef.current);
        }
      }
      
      // Create MediaRecorder
      const recorder = new MediaRecorder(mediaStream, {
        mimeType: 'audio/webm;codecs=opus'
      });
      
      mediaRecorderRef.current = recorder;
      
      // Set up event handlers
      recorder.ondataavailable = (event) => {
        console.log('Data available:', event.data.size, 'bytes');
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      recorder.onstop = () => {
        console.log('Recording stopped, creating final blob...');
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        console.log('Final blob size:', audioBlob.size, 'bytes');
        
        // Send audio data to parent component
        onAudioData(audioBlob);
        
        // Clear audio chunks
        audioChunksRef.current = [];
      };
      
      recorder.onerror = (event) => {
        console.error('MediaRecorder error:', event);
        setError('Recording error occurred');
      };
      
      // Start recording
      recorder.start(100); // Collect data every 100ms
      
      console.log('VoiceRecorder: calling onRecordingStateChange(true)');
      onRecordingStateChange(true);
      setRecordingTime(0);
      
      // Start timer
      recordingTimerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
      
    } catch (err) {
      console.error('Error starting recording:', err);
      setError('Failed to access microphone. Please check permissions.');
      onRecordingStateChange(false);
    }
  }, [onAudioData, onRecordingStateChange, initializeAudioContext, onAudioContextReady]);

  // Stop recording
  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      console.log('Stopping recording...');
      
      // Request any remaining data before stopping
      mediaRecorderRef.current.requestData();
      mediaRecorderRef.current.stop();
    }
    
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
    
    // Clean up audio context if it exists and is not closed
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      try {
        audioContextRef.current.close();
        audioContextRef.current = null;
        analyserRef.current = null;
      } catch (err) {
        console.warn('Error closing AudioContext:', err);
      }
    }
    
    console.log('VoiceRecorder: calling onRecordingStateChange(false)');
    onRecordingStateChange(false);
    setIsPaused(false);
  }, [onRecordingStateChange]);

  // Pause/Resume recording
  const togglePause = useCallback(() => {
    if (!mediaRecorderRef.current) return;
    
    if (mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.pause();
      setIsPaused(true);
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
        recordingTimerRef.current = null;
      }
    } else if (mediaRecorderRef.current.state === 'paused') {
      mediaRecorderRef.current.resume();
      setIsPaused(false);
      recordingTimerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
    }
  }, []);

  // Draw audio visualization
  const drawVisualization = useCallback(() => {
    const targetCanvas = externalCanvasRef?.current || canvasRef.current;
    if (!targetCanvas || !analyserRef.current) return;
    
    const canvas = targetCanvas;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    const analyser = analyserRef.current;
    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);
    
    const draw = () => {
      if (!isRecording) return; // Use isRecording prop
      
      requestAnimationFrame(draw);
      analyser.getByteFrequencyData(dataArray);
      
      ctx.fillStyle = 'rgb(0, 0, 0)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      const barWidth = (canvas.width / bufferLength) * 2.5;
      let barHeight;
      let x = 0;
      
      for (let i = 0; i < bufferLength; i++) {
        barHeight = dataArray[i] / 2;
        
        const gradient = ctx.createLinearGradient(0, 0, 0, canvas.height);
        gradient.addColorStop(0, '#3b82f6');
        gradient.addColorStop(1, '#1d4ed8');
        
        ctx.fillStyle = gradient;
        ctx.fillRect(x, canvas.height - barHeight, barWidth, barHeight);
        
        x += barWidth + 1;
      }
    };
    
    draw();
  }, [isRecording, externalCanvasRef]);

  // Format recording time
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Handle recording button click
  const handleRecordingToggle = () => {
    console.log('VoiceRecorder: handleRecordingToggle called, current isRecording:', isRecording);
    
    if (isRecording) {
      // Always allow stopping recording, even when disabled
      stopRecording();
    } else {
      if (disabled) {
        setError('Recording is disabled');
        return;
      }
      startRecording();
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        try {
          audioContextRef.current.close();
        } catch (err) {
          console.warn('AudioContext already closed or closing:', err);
        }
      }
    };
  }, []);

  // Start visualization when recording
  useEffect(() => {
    if (isRecording) {
      drawVisualization();
    }
  }, [isRecording, drawVisualization]);

  return (
    <div className={`flex ${compact ? 'items-center' : 'flex-col items-center'} space-y-${compact ? '0' : '4'}`}>
      {/* Audio Visualization - Only show in full mode */}
      {!compact && (
        <div className="relative">
          <canvas
            ref={canvasRef}
            width={200}
            height={60}
            className={`rounded-lg border-1 ${
              isRecording 
                ? 'bg-gray-100' 
                : 'border-gray-300 bg-gray-100'
            }`}
          />
          {isRecording && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-black text-sm font-medium">
                {formatTime(recordingTime)}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Recording Controls */}
      <div className={`flex items-center ${compact ? 'space-x-2' : 'space-x-4'}`}>
        {/* Main Record Button */}
        <button
          onClick={handleRecordingToggle}
          disabled={disabled && !isRecording}
          className={`${compact ? 'w-20 h-20' : 'w-16 h-16'} rounded-full flex items-center justify-center transition-all 
          duration-200 hover:shadow-lg ${
            isRecording
              ? 'bg-red-500 hover:bg-red-600 text-white'
              : 'text-white'
          } ${
            (disabled && !isRecording) ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
          }`}
          style={{
            background: isRecording 
              ? undefined 
              : 'linear-gradient(to right,#4e54c8,#8f94fb)'
          }}
        >
          {isRecording ? (
            <svg 
              className={`${compact ? 'w-10 h-10' : 'w-10 h-10'}`}
              fill="currentColor" 
              viewBox="0 0 24 24"
            >
              <rect x="6" y="6" width="12" height="12" />
            </svg>
          ) : (
            <Microphone
              className={`${compact ? 'w-10 h-10' : 'w-10 h-10'}`}
              strokeWidth={1.5} 
            />
          )}
        </button>

        {/* Pause/Resume Button - Only show in full mode */}
        {isRecording && !compact && (
          <button
            onClick={togglePause}
            className="w-12 h-12 rounded-full bg-gray-500 hover:bg-gray-600 text-white flex items-center justify-center"
          >
            {isPaused ? (
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path d="M8 5v10l8-5-8-5z" />
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <rect x="5" y="4" width="3" height="12" />
                <rect x="12" y="4" width="3" height="12" />
              </svg>
            )}
          </button>
        )}
      </div>

      {/* Status and Error Messages - Only show in full mode */}
      {!compact && (
        <div className="text-center">
          {error && (
            <div className="text-red-600 text-sm mb-2">{error}</div>
          )}
          <div className="text-sm text-gray-600">
            {isRecording 
              ? isPaused 
                ? 'Recording paused' 
                : 'Recording...'
              : 'Click to start recording'
            }
          </div>
        </div>
      )}
    </div>
  );
};

export default VoiceRecorder; 